import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, TextInput, Badge, ActionIcon } from '@mantine/core';
import { IconArrowLeft, IconPlus, IconPackages, IconCheck, IconTrash, IconEdit, IconSettings } from '@tabler/icons-react';
import { openConfirmModal } from '@mantine/modals';
import PageHeader from '@/components/page-header';
import CardModal from '@/components/card-modal';
import { SkeletonList } from '@/components/snippets';
import { setWebTitle } from '@/lib/format-utils';
import { useProduct, useUpdateProduct, useServices, useDeleteProduct } from '@/hooks/api-hooks';
import { PRODUCTS_PATH } from '@/lib/navigation';
import { Service } from '@turizi/shared';
import ServicesTable from '@/components/services-table';
import SearchInput from '@/components/search-input';
import { multiWordSearch } from '@/utils/search-utils';
import { ServiceToString } from '@/lib/models-utils';
import ServiceCardPrivate from '@/components/service-card-private';
import { TagIcon } from '@/lib/tag-utils';
import { ServiceTitle, ServiceTitleStatic } from '@/components/services/service-title';

export default function ProductEditPage(): JSX.Element {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const productId = Number(id);

    const {
        data: productResponse,
        isLoading: isLoadingProduct
    } = useProduct(productId);
    const updateProduct = useUpdateProduct();
    const deleteProduct = useDeleteProduct();

    const handleDelete = () => {
        openConfirmModal({
            title: t('delete_product'),
            children: <div className="text-sm">{t('confirm_delete_product')}</div>,
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                if (!productId) return;
                try {
                    await deleteProduct.mutateAsync(productId);
                    navigate(PRODUCTS_PATH);
                } catch (error) {
                    console.error('Failed to delete product', error);
                }
            }
        });
    };

    const [addModalOpened, setAddModalOpened] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const {
        isLoading: isLoadingServices,
        data: servicesData
    } = useServices(
        {},
        { enabled: addModalOpened }
    );

    const product = productResponse?.data;
    const allServices: Service[] = useMemo(() => servicesData?.data || [], [servicesData?.data]);

    const [selectedServiceIds, setSelectedServiceIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (product?.services) {
            setSelectedServiceIds(new Set(product.services.map((s) => s.id)));
        }
    }, [product?.services]);

    const linkedServices = useMemo(() => {
        if (allServices.length > 0) {
            return allServices.filter((s) => selectedServiceIds.has(s.id));
        }
        return product?.services || [];
    }, [allServices, selectedServiceIds, product?.services]);

    const filteredServices = useMemo(() => {
        if (!searchQuery.trim()) return allServices;
        return allServices.filter((service) => {
            const title = multiWordSearch(ServiceToString(service, t), searchQuery) || false;
            const _id = service.id.toString() === searchQuery.trim();
            return title || _id;
        });
    }, [searchQuery, allServices, t]);

    const [nameDraft, setNameDraft] = useState('');

    setWebTitle(product?.name || t('edit_product'));

    useEffect(() => {
        if (product) {
            setNameDraft(product.name || '');
        }
    }, [product]);

    const toggleService = (serviceId: number) => {
        if (!productId) return;

        setSelectedServiceIds((prev) => {
            const next = new Set(prev);
            if (next.has(serviceId)) {
                next.delete(serviceId);
            } else {
                next.add(serviceId);
            }
            const nextIds = Array.from(next);

            updateProduct.mutate({
                id: productId,
                service_ids: nextIds,
            });

            return next;
        });
    };

    const selectAllServices = () => {
        if (!productId || allServices.length === 0) return;
        const allIds = allServices.map((s) => s.id);
        const isAllSelected = allServices.every((s) => selectedServiceIds.has(s.id));
        const nextIds = isAllSelected ? [] : allIds;

        setSelectedServiceIds(new Set(nextIds));

        updateProduct.mutate({
            id: productId,
            service_ids: nextIds,
        });
    };

    const handleSaveName = async () => {
        if (!nameDraft.trim() || !productId) return;
        try {
            await updateProduct.mutateAsync({
                id: productId,
                name: nameDraft.trim(),
            });
        } catch (error) {
            console.error('Failed to update product name', error);
        }
    };

    if (isLoadingProduct || !product) {
        return (
            <div>
                <PageHeader title={t('edit_product')} />
                <div className="panel-content">
                    <SkeletonList />
                </div>
            </div>
        );
    }

    const isNameChanged = nameDraft.trim() !== (product.name || '');

    return (
        <div>
            <PageHeader
                title={product.name || t('edit_product')}
                rightSection={
                    <Button
                        variant="default"
                        size="xs"
                        radius="xl"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate(PRODUCTS_PATH)}
                    >
                        {t('back')}
                    </Button>
                }
            />

            <div className="panel-content space-y-6">
                {/* Basic Info with Inline Save Button */}
                <div className="solid-box-border p-4 space-y-4">
                    <div className="flex gap-2 items-end max-w-md">
                        <TextInput
                            label={t('product_name')}
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            disabled={updateProduct.isPending}
                            className="font-bold flex-1"
                            size="lg"
                        />
                        {isNameChanged && (
                            <Button
                                color="black"
                                size="xs"
                                radius="md"
                                leftSection={<IconCheck size={16} />}
                                disabled={!nameDraft.trim()}
                                loading={updateProduct.isPending}
                                onClick={handleSaveName}
                            >
                                {t('save')}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Linked Services Section */}
                <div className="solid-box-border p-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="font-bold text-sm text-slate-900">{t('linked_services')}</div>
                            <div className="text-xs text-gray-500">
                                {t('selected_services_count', { count: linkedServices.length })}
                            </div>
                        </div>
                        <Button
                            leftSection={<IconSettings size={16} />}
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => setAddModalOpened(true)}
                        >
                            {t('edit')} {t('services')}
                        </Button>
                    </div>

                    {/* Linked Services List */}
                    <ServicesTable
                        services={linkedServices}
                    />
                </div>
            </div>

            {/* Delete Product Section */}
            <div className="flex justify-end pt-4">
                <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    leftSection={<IconTrash size={16} />}
                    loading={deleteProduct.isPending}
                    onClick={handleDelete}
                >
                    {t('delete_product')}
                </Button>
            </div>

            {/* Add Service Modal using CardModal */}
            <CardModal
                opened={addModalOpened}
                onClose={() => setAddModalOpened(false)}
                size="90%"
                header={
                    <div className="flex items-center justify-between">
                        <div className="font-bold text-base text-slate-900">
                            {t('add_service')}
                        </div>
                        <Badge variant="light" color="blue" size="sm">
                            {t('selected_services_count', { count: linkedServices.length })}
                        </Badge>
                    </div>
                }
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            color="black"
                            size="xs"
                            radius="xl"
                            onClick={() => setAddModalOpened(false)}
                        >
                            {t('done')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    {isLoadingServices ? (
                        <SkeletonList />
                    ) : (
                        <>
                            <div className="flex gap-2 items-center">
                                <SearchInput
                                    onSearch={setSearchQuery}
                                    size="xs"
                                />
                                <div className="text-xs text-gray-400 font-bold">
                                    ({filteredServices.length})
                                </div>
                            </div>
                            <ServicesTable
                                services={filteredServices}
                                rawServices={allServices}
                                enableBulkActions={true}
                                selectedServices={Array.from(selectedServiceIds).map(String)}
                                toggleServiceSelection={(serviceIdStr) => toggleService(Number(serviceIdStr))}
                                onSelectService={(service) => toggleService(service.id)}
                                selectAll={selectAllServices}
                            />
                        </>
                    )}
                </div>
            </CardModal>
        </div>
    );
}
